'use client';

import { useId, useState } from 'react';
import Media from 'components/shared/Media/Media';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { Colorways, Margins, ColorTheme } from 'types/constants';
import { cn, getColorway, getYMargins } from 'utils/styles';
import type { TabbedMediaCopyProps } from './types';

const TabbedMediaCopy = ({ sys, colorTheme, tabs, sectionName }: TabbedMediaCopyProps) => {
  // Contentful returns null placeholders for missing/unpublished linked tabs;
  // drop them so the 2-tab guard and rendering never touch a null entry.
  const items = (tabs?.items ?? []).filter(Boolean);
  const [activeIndex, setActiveIndex] = useState(0);
  const baseId = useId();

  if (items.length < 2) return null;

  const activeTab = items[activeIndex] ?? items[0];
  const tabId = (index: number) => `${baseId}-tab-${index}`;
  const panelId = `${baseId}-panel`;

  // Both themes sit on a white section; the theme only flips the inner card.
  const isDark = colorTheme === ColorTheme.PrimaryDark;
  const cardColorway = isDark ? Colorways.Inverse : Colorways.Nickel;

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={cn(
        getColorway(Colorways.Primary),
        getYMargins(Margins.Centered),
        sectionName && 'scroll-mt-(--height-nav)',
      )}
      data-testid="tabbed-media-copy">
      <div className="content-wrapper">
        <div
          className={cn(
            getColorway(cardColorway),
            'flex flex-col overflow-hidden rounded-2xl md:h-[620px] md:flex-row md:items-center md:rounded-3xl',
          )}>
          {/* Copy panel */}
          <div className="flex w-full flex-col gap-8 px-6 py-8 md:h-full md:w-1/2 md:px-14 md:py-16">
            <div
              role="tablist"
              aria-label="Content tabs"
              className="flex w-full sm:max-w-[294px] items-stretch overflow-hidden rounded-md border border-primary in-cw-dark-colorways:border-white md:w-fit md:max-w-none"
              {...liveUpdateAttrs(sys?.id, 'tabs')}>
              {items.map((tab, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={tab.sys?.id ?? index}
                    type="button"
                    role="tab"
                    id={tabId(index)}
                    aria-selected={isActive}
                    aria-controls={panelId}
                    onClick={() => setActiveIndex(index)}
                    className={cn(
                      'flex flex-1 p-2.5 items-center justify-center rounded-md px-6 text-center font-sans text-label-saans transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current md:w-[180px] md:flex-none cursor-pointer',
                      isActive ? 'bg-[#15744a] text-[#ffffff]' : 'text-current hover:bg-current/5',
                    )}
                    {...liveUpdateAttrs(tab.sys?.id, 'tabCopy')}>
                    <RichTextCopy copy={tab.tabCopy} inline />
                  </button>
                );
              })}
            </div>
            <div
              id={panelId}
              role="tabpanel"
              aria-labelledby={tabId(activeIndex)}
              className="flex flex-col gap-4 md:flex-1 md:justify-center">
              {activeTab.headline && (
                <div
                  className="font-sans text-heading-sm md:text-heading-md"
                  {...liveUpdateAttrs(activeTab.sys?.id, 'headline')}>
                  <RichTextHeadline headline={activeTab.headline} level="h3" />
                </div>
              )}
              {activeTab.copy && (
                <div className="font-sans text-label-saans font-medium" {...liveUpdateAttrs(activeTab.sys?.id, 'copy')}>
                  <RichTextCopy copy={activeTab.copy} ySpacing="none" />
                </div>
              )}
            </div>
          </div>

          {/* Media panel */}
          <div
            className="relative aspect-4/3 w-full overflow-hidden bg-reserve md:aspect-auto md:h-full md:w-1/2"
            {...liveUpdateAttrs(activeTab.sys?.id, 'media')}>
            {activeTab.media?.desktopImage?.url && (
              <Media
                media={{ ...activeTab.media.desktopImage, fill: true }}
                mobileMedia={activeTab.media.mobileImage}
                roundedImage={false}
                className="object-cover sm:object-contain"
                sizes="(min-width: 1025px) 50vw, 100vw"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TabbedMediaCopy;
